import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import ImageUploader from "@/admin/ImageUploader";

const EMPTY = {
  kind: "doctor",
  plan: "free",
  name: "",
  senacyt: "",
  country: "EC",
  province: "",
  city: "",
  zone: "",
  specialties: [],
  photos: [],
  gallery: [],
  description: "",
  clinic_id: "",
  clinic_name: "",
  own_clinic: false,
  address: "",
  whatsapp: "",
  instagram: "",
  map_url: "",
  insurances: [],
  hours: "",
  schedule: [],
  tags: [],
  position: 1000,
  is_hidden: false,
  date: "",
};

const field = "w-full rounded-xl border border-neutral-200 px-3 py-2.5 text-sm outline-none focus:border-neutral-900";
const lbl = "mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500";
const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function Chips({ label, options, value, onToggle, testid }) {
  return (
    <div>
      <span className={lbl}>{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button
              type="button"
              key={o}
              data-testid={`${testid}-${o}`}
              onClick={() => onToggle(o)}
              className={`rounded-full px-3 py-1.5 text-sm ${
                active ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-700"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ProfessionalForm({ initial, locations, specialties, insurances, clinics, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [specialtyOpts, setSpecialtyOpts] = useState(specialties || []);

  useEffect(() => {
    setF(initial ? { ...EMPTY, ...initial, clinic_id: initial.clinic_id || "" } : EMPTY);
  }, [initial]);

  useEffect(() => {
    api.get(`/specialties?country=${f.country}`).then((r) => setSpecialtyOpts(r.data)).catch(() => {});
  }, [f.country]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, o) =>
    setF((p) => ({ ...p, [k]: p[k].includes(o) ? p[k].filter((x) => x !== o) : [...p[k], o] }));

  const provinces = useMemo(() => locations.filter((l) => l.country === f.country), [locations, f.country]);
  const cities = useMemo(() => provinces.find((l) => l.province === f.province)?.cities || [], [provinces, f.province]);
  const cityZones = useMemo(() => {
    const loc = provinces.find((l) => l.province === f.province);
    return (loc?.city_zones || {})[f.city] || [];
  }, [provinces, f.province, f.city]);
  const insOptions = useMemo(
    () => insurances.filter((i) => i.country === f.country).map((i) => i.name),
    [insurances, f.country]
  );
  const clinicOptions = useMemo(() => clinics.filter((c) => c.id !== f.id), [clinics, f.id]);
  const mapQ = f.address || f.map_url;
  const mapPreviewSrc = mapQ ? `https://www.google.com/maps?q=${encodeURIComponent(mapQ)}&output=embed` : null;

  const save = async () => {
    if (!f.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    const payload = { ...f, position: Number(f.position) || 1000 };
    if (payload.clinic_id) {
      const c = clinics.find((x) => x.id === payload.clinic_id);
      if (c) payload.clinic_name = c.name;
    } else {
      payload.clinic_id = null;
    }
    try {
      if (f.id) await api.put(`/admin/professionals/${f.id}`, payload);
      else await api.post("/admin/professionals", payload);
      toast.success("Guardado");
      onSaved();
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose}>
      <div
        className="no-scrollbar max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="professional-form"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-brand text-xl font-semibold">{f.id ? "Editar" : "Nuevo"} profesional</h2>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-neutral-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className={lbl}>Tipo</span>
              <select data-testid="form-kind" className={field} value={f.kind} onChange={(e) => set("kind", e.target.value)}>
                <option value="doctor">Odontólogo</option>
                <option value="clinic">Clínica</option>
              </select>
            </div>
            <div>
              <span className={lbl}>Plan</span>
              <select data-testid="form-plan" className={field} value={f.plan} onChange={(e) => set("plan", e.target.value)}>
                <option value="free">Free</option>
                <option value="verified">Verificado</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className={lbl}>Nombre</span>
              <input data-testid="form-name" className={field} value={f.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <span className={lbl}>{f.country === "AR" ? "Matrícula" : "Registro Senecyt"}</span>
              <input className={field} value={f.senacyt} onChange={(e) => set("senacyt", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className={lbl}>País</span>
              <select data-testid="form-country" className={field} value={f.country} onChange={(e) => { set("country", e.target.value); set("province", ""); set("city", ""); }}>
                <option value="EC">Ecuador</option>
                <option value="AR">Argentina</option>
              </select>
            </div>
            <div>
              <span className={lbl}>Provincia</span>
              <select className={field} value={f.province} onChange={(e) => { set("province", e.target.value); set("city", ""); }}>
                <option value="">—</option>
                {provinces.map((p) => (
                  <option key={p.province} value={p.province}>{p.province}</option>
                ))}
              </select>
            </div>
            <div>
              <span className={lbl}>Ciudad</span>
              <select className={field} value={f.city} onChange={(e) => { set("city", e.target.value); set("zone", ""); }}>
                <option value="">—</option>
                {cities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {cityZones.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className={lbl}>Zona ({f.city})</span>
                <select data-testid="form-zone" className={field} value={f.zone} onChange={(e) => set("zone", e.target.value)}>
                  <option value="">— Sin zona —</option>
                  {cityZones.map((z) => (
                    <option key={z} value={z}>{z}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <Chips label="Especialidades" options={specialtyOpts} value={f.specialties} onToggle={(o) => toggle("specialties", o)} testid="spec" />

          <div>
            <span className={lbl}>Descripción</span>
            <textarea className={field} rows={6} value={f.description} onChange={(e) => set("description", e.target.value)} />
          </div>

          {f.kind === "doctor" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className={lbl}>Clínica vinculada (etiqueta)</span>
                <select
                  data-testid="form-clinic"
                  className={field}
                  value={f.own_clinic ? "__own__" : f.clinic_id}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__own__") {
                      setF((p) => ({ ...p, own_clinic: true, clinic_id: "", clinic_name: "Clínica propia" }));
                    } else {
                      setF((p) => ({ ...p, own_clinic: false, clinic_id: v, clinic_name: v ? "" : p.clinic_name }));
                    }
                  }}
                >
                  <option value="">— Ninguna / texto libre —</option>
                  <option value="__own__">Clínica propia (sin vincular)</option>
                  {clinicOptions.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <span className={lbl}>Lugar de trabajo (texto)</span>
                <input className={field} value={f.clinic_name} disabled={!!f.clinic_id || f.own_clinic} onChange={(e) => set("clinic_name", e.target.value)} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className={lbl}>WhatsApp (solo números)</span>
              <input data-testid="form-whatsapp" className={field} value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="593999123456" />
            </div>
            <div>
              <span className={lbl}>Instagram (URL)</span>
              <input className={field} value={f.instagram} onChange={(e) => set("instagram", e.target.value)} />
            </div>
          </div>

          {/* Ubicación / Google Maps */}
          <div className="rounded-2xl border border-neutral-200 p-3">
            <span className={lbl}>Ubicación — Enlace de Google Maps</span>
            <input
              data-testid="form-map"
              className={field}
              value={f.map_url}
              onChange={(e) => set("map_url", e.target.value)}
              placeholder="https://maps.app.goo.gl/... o https://maps.google.com/..."
            />
            <p className="mt-1 text-xs text-neutral-400">
              Pega el enlace de Google Maps del consultorio (así se detecta rápido). Opcionalmente escribe la dirección.
            </p>
            <div className="mt-3">
              <span className={lbl}>Dirección (texto, opcional)</span>
              <input className={field} value={f.address} onChange={(e) => set("address", e.target.value)} />
            </div>
            {mapPreviewSrc ? (
              <div className="mt-3 overflow-hidden rounded-xl ring-1 ring-neutral-200" data-testid="map-preview">
                <iframe title="Vista previa del mapa" src={mapPreviewSrc} className="h-44 w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            ) : (
              <div className="mt-3 grid h-44 place-items-center rounded-xl bg-neutral-50 text-sm text-neutral-400">
                Pega un enlace o dirección para ver el mapa
              </div>
            )}
          </div>

          <div>
            <span className={lbl}>Horario de atención (días y horas)</span>
            <div className="space-y-2 rounded-2xl border border-neutral-200 p-3">
              {DAYS.map((day) => {
                const entry = (f.schedule || []).find((s) => s.day === day);
                return (
                  <div key={day} className="flex items-center gap-3">
                    <label className="flex w-28 shrink-0 items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        data-testid={`day-${day}`}
                        checked={!!entry}
                        onChange={() =>
                          setF((p) =>
                            entry
                              ? { ...p, schedule: p.schedule.filter((s) => s.day !== day) }
                              : { ...p, schedule: [...(p.schedule || []), { day, from: "09:00", to: "18:00" }] }
                          )
                        }
                      />
                      {day}
                    </label>
                    {entry ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          className={`${field} w-32`}
                          value={entry.from}
                          onChange={(e) => setF((p) => ({ ...p, schedule: p.schedule.map((s) => (s.day === day ? { ...s, from: e.target.value } : s)) }))}
                        />
                        <span className="text-neutral-400">—</span>
                        <input
                          type="time"
                          className={`${field} w-32`}
                          value={entry.to}
                          onChange={(e) => setF((p) => ({ ...p, schedule: p.schedule.map((s) => (s.day === day ? { ...s, to: e.target.value } : s)) }))}
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-neutral-300">Cerrado</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Chips label="Cobertura (aseguradoras)" options={insOptions} value={f.insurances} onToggle={(o) => toggle("insurances", o)} testid="ins" />

          {/* Tags */}
          <div>
            <span className={lbl}>Etiquetas (chips) · máx 3</span>
            <div className="mb-2 flex flex-wrap gap-2">
              {f.tags.map((t) => (
                <span key={t} data-testid={`tag-${t}`} className="flex items-center gap-1 rounded-full bg-neutral-900 px-3 py-1 text-sm text-white">
                  {t}
                  <button type="button" data-testid={`remove-tag-${t}`} onClick={() => set("tags", f.tags.filter((x) => x !== t))}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="tag-input"
                className={field}
                value={tagInput}
                disabled={f.tags.length >= 3}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const v = tagInput.trim();
                    if (v && f.tags.length < 3 && !f.tags.includes(v)) set("tags", [...f.tags, v]);
                    setTagInput("");
                  }
                }}
                placeholder={f.tags.length >= 3 ? "Máximo 3 etiquetas" : "Escribe y Enter"}
              />
            </div>
          </div>

          {/* Photos */}
          <div>
            <span className={lbl}>Fotos principales (carrusel){f.plan === "free" ? " · máx 1" : " · máx 4"}</span>
            <ImageUploader value={f.photos} onChange={(v) => set("photos", v)} max={f.plan === "free" ? 1 : 4} single={f.plan === "free"} testid="photos" hint="Vertical 4:5 (ej. 1080×1350 px). Máx HD; se comprime automáticamente." />
          </div>
          {f.plan === "verified" && (
            <div>
              <span className={lbl}>Galería (máx 4)</span>
              <ImageUploader value={f.gallery} onChange={(v) => set("gallery", v)} max={4} testid="gallery" hint="Vertical 3:4 (ej. 900×1200 px). Máx HD." />
            </div>
          )}

          <div className="grid grid-cols-3 items-end gap-4">
            <div>
              <span className={lbl}>Posición (menor = arriba)</span>
              <input data-testid="form-position" type="number" className={field} value={f.position} onChange={(e) => set("position", e.target.value)} />
            </div>
            <div>
              <span className={lbl}>Fecha</span>
              <input type="date" className={field} value={f.date} onChange={(e) => set("date", e.target.value)} />
            </div>
            <label className="flex items-center gap-2 pb-2 text-sm">
              <input type="checkbox" checked={f.is_hidden} onChange={(e) => set("is_hidden", e.target.checked)} />
              Oculto
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-xl px-5 py-2.5 text-neutral-600">Cancelar</button>
          <button
            data-testid="form-save-btn"
            onClick={save}
            disabled={saving}
            className="rounded-xl bg-neutral-900 px-6 py-2.5 font-medium text-white hover:bg-neutral-800 disabled:opacity-60"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
